import {Color} from '@benchling/nucleus-ui';
import {Badge, createStyles} from '@mantine/core';
import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';

import BenchlingIcon, {BenchlingIconName} from './icons/BenchlingIcon';

export interface TagProps {
  /** Intentionally not rendered when size='SMALL' */
  icon?: BenchlingIconName | null;
  /** @default TagSize.NORMAL */
  size?: TagSize;
  text: string;
  /** @default TagColor.DEFAULT */
  color?: TagColor;
}

/**
 * @deprecated Use Mantine `Badge` instead.
 * This component displays basic data tags and attributes inline with text.
 */
export default function Tag({
  icon = null,
  size = TagSize.NORMAL,
  color = TagColor.DEFAULT,
  text,
}: TagProps): JSX.Element {
  const {classes} = useStyles({size, color});

  const leftSection =
    icon != null && size === TagSize.NORMAL ? (
      <BenchlingIcon dataTestAttrs={{component: 'Tag', element: 'icon'}} icon={icon} />
    ) : undefined;

  return (
    <Badge
      classNames={{root: classes.tag, leftSection: classes.spacerRight, inner: classes.tagContent}}
      data-amp-safe={false}
      leftSection={leftSection}
      size={size === TagSize.SMALL ? 'sm' : 'lg'}
      {...getTestProps({component: 'Tag', key: text})}
    >
      {text}
    </Badge>
  );
}

export enum TagSize {
  SMALL = 'SMALL',
  NORMAL = 'NORMAL',
}

export enum TagColor {
  DEFAULT = 'DEFAULT',
  SECONDARY = 'SECONDARY',
  WARN = 'WARN',
  ERROR = 'ERROR',
  SUCCESS = 'SUCCESS',
  GRAPE = 'GRAPE',
  PINK = 'PINK',
  TEAL = 'TEAL',
  LIME = 'LIME',
  BETA = 'BETA',
}

const useStyles = createStyles((theme, {size, color}: {size: TagSize; color: TagColor}) => {
  const tagSizeStyle: Record<TagSize, Record<string, string | number>> = {
    [TagSize.SMALL]: {
      height: '21px',
      marginRight: theme.spacing.sm,
      padding: `${theme.spacing.xs} ${theme.spacing.sm} !important`,
    },
    [TagSize.NORMAL]: {
      marginRight: theme.spacing.sm,
      padding: `${theme.spacing.xs} ${theme.spacing.sm} !important`,
    },
  };

  const tagFontStyle: Record<TagSize, Record<string, string | number>> = {
    [TagSize.SMALL]: {
      fontSize: theme.fontSizes.xs,
      lineHeight: '13px',
    },
    [TagSize.NORMAL]: {
      fontSize: theme.fontSizes.md,
      lineHeight: '16px',
    },
  };

  const tagColorStyle: Record<TagColor, Record<string, string | number>> = {
    [TagColor.DEFAULT]: {
      backgroundColor: Color.BLUE_150,
      color: Color.BLUE_600,
    },
    [TagColor.SECONDARY]: {
      backgroundColor: Color.GRAY_100,
      color: Color.GRAY_900,
    },
    [TagColor.WARN]: {
      backgroundColor: Color.ORANGE_200,
      color: Color.ORANGE_600,
    },
    [TagColor.ERROR]: {
      backgroundColor: Color.RED_200,
      color: Color.RED_600,
    },
    [TagColor.SUCCESS]: {
      backgroundColor: Color.GREEN_200,
      color: Color.GRAY_700,
    },
    [TagColor.GRAPE]: {
      backgroundColor: Color.GRAPE_200,
      color: Color.GRAPE_600,
    },
    [TagColor.PINK]: {
      backgroundColor: Color.PINK_200,
      color: Color.PINK_600,
    },
    [TagColor.TEAL]: {
      backgroundColor: Color.TEAL_200,
      color: Color.TEAL_600,
    },
    [TagColor.LIME]: {
      backgroundColor: Color.LIME_200,
      color: Color.GRAY_700,
    },
    [TagColor.BETA]: {
      backgroundColor: Color.YELLOW_200,
      color: Color.YELLOW_600,
    },
  };

  return {
    tag: {
      alignItems: 'center',
      borderRadius: theme.spacing.xs,
      display: 'inline-flex',
      ...tagSizeStyle[size],
      ...tagColorStyle[color],
    },
    tagContent: {
      alignItems: 'center',
      display: 'flex',
      fontFamily: theme.fontFamily,
      fontWeight: theme.other.fontWeights.semibold,
      ...tagFontStyle[size],
    },
    spacerRight: {
      marginRight: theme.spacing.xs,
    },
  };
});
